import React, { useCallback, useRef } from 'react';
import { FocusOn } from 'react-focus-on';
import { isElementInTheViewport } from '../../functions.web';
// A child dialog can outlive the menu item that opened it. Retain the menu's
// logical opener without keeping closed focus-scope DOM nodes alive.
const returnTargets = new WeakMap();
function findReturnTarget(focused) {
    let ancestor = focused;
    while (ancestor) {
        const target = returnTargets.get(ancestor);
        if (target) {
            return target;
        }
        ancestor = ancestor.parentElement;
    }
    return focused;
}
function canRestoreFocus(target) {
    if (!(target instanceof HTMLElement) || target === document.body
        || target === document.documentElement || !isElementInTheViewport(target)
        || !target.getClientRects().length || target.closest('[inert]')
        || target.hasAttribute('disabled')) {
        return false;
    }
    const { visibility } = getComputedStyle(target);
    return visibility !== 'hidden' && visibility !== 'collapse';
}
/** Preserve a surviving trigger, or its containing menu's logical opener. */
export default function DialogFocus({ enabled = true, returnFocus = true, ...props }) {
    const logicalTarget = useRef(null);
    const committedEnabled = useRef(false);
    // Capture before the focus-lock commit moves focus into the new scope.
    // Publish only from the committed ref, so abandoned renders cannot change
    // another scope's return target. FocusOn's onActivation callback is too late.
    const origin = enabled && !committedEnabled.current && typeof document !== 'undefined'
        ? document.activeElement : null;
    const captureScope = useCallback((node) => {
        if (!node) {
            return;
        }
        if (enabled && !committedEnabled.current) {
            logicalTarget.current = origin instanceof HTMLElement && origin !== document.body
                && origin !== document.documentElement ? findReturnTarget(origin) : null;
        }
        committedEnabled.current = enabled;
        if (enabled && logicalTarget.current) {
            returnTargets.set(node, logicalTarget.current);
        }
        else {
            returnTargets.delete(node);
        }
    }, [enabled, origin]);
    const restoreFocus = useCallback((target) => {
        const policy = typeof returnFocus === 'function' ? returnFocus(target) : returnFocus;
        if (policy && canRestoreFocus(target)) {
            return { preventScroll: true, ...(typeof policy === 'object' ? policy : {}) };
        }
        const fallback = logicalTarget.current;
        const fallbackPolicy = fallback && (typeof returnFocus === 'function' ? returnFocus(fallback) : returnFocus);
        if (fallback !== target && fallbackPolicy && canRestoreFocus(fallback)) {
            fallback.focus({ preventScroll: true, ...(typeof fallbackPolicy === 'object' ? fallbackPolicy : {}) });
        }
        return false;
    }, [returnFocus]);
    return (React.createElement(FocusOn, { ...props, enabled: enabled, ref: captureScope, returnFocus: restoreFocus }));
}
